import C from "classnames";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { useRef, useState, useEffect } from "react";
import { enrollInTournament } from "sj-ui-toolkit/src/api/userTournaments";
import { buildRoute } from "sj-ui-toolkit/src/constants/routes";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";

import S from "./DashboardCard.module.scss";

interface TournamentCardProps {
  id: number;
  name: string;
  summary: string;
  finishDate: string;
  tournamentUserPoints: number;
  completedExercises: number;
  exercisesCount: number;
  enrolled: boolean;
}

export const TournamentCard = ({
  id,
  name,
  summary,
  finishDate,
  tournamentUserPoints,
  completedExercises,
  exercisesCount,
  enrolled,
}: TournamentCardProps) => {
  const { navigateTo } = useSmartNavigation();
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [isClamped, setIsClamped] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);

  const description = summary || "Complete tournament challenges";

  useEffect(() => {
    const element = descriptionRef.current;
    if (element) {
      setIsClamped(element.scrollHeight > element.clientHeight);
    }
  }, [description]);

  const navigateToTournament = () => {
    navigateTo(
      buildRoute("Learn.routes.tournaments.routes.details.route", { tournamentId: String(id) })
    );
  };

  const handleEnrollAndNavigate = async () => {
    setIsEnrolling(true);
    try {
      await enrollInTournament(id);
      navigateToTournament();
    } catch (_error) {
      setIsEnrolling(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    } catch (_error) {
      return dateString;
    }
  };

  const progress = exercisesCount > 0 ? Math.round((completedExercises / exercisesCount) * 100) : 0;

  return (
    <div
      className={C(S.assessmentCard, { [S.notClickable]: !enrolled })}
      onClick={enrolled ? navigateToTournament : undefined}
    >
      <svg className={S.completionBadge} width="90" height="101" viewBox="0 0 90 101" fill="none">
        <circle cx="61" cy="40" r="60.1286" stroke="#9EA0A2" strokeWidth="1.74286" />
        <circle cx="60.9996" cy="39.9994" r="49.9297" stroke="#9EA0A2" strokeWidth="1.74286" />
        <path
          d="M38.4851 44.0536L49.7456 59.5895L83.5197 20.4121"
          stroke="#9EA0A2"
          strokeWidth="1.74286"
        />
      </svg>
      <div className={S.assessmentCardHeader}>
        <Tag value="Tournament" severity="info" rounded={true} />
        {tournamentUserPoints > 0 && (
          <span className={C(S.mutedText, "atlas-h5")}> {tournamentUserPoints} pts</span>
        )}
      </div>
      <div className={S.assessmentCardBody}>
        <h3 className="atlas-h4">{name}</h3>
        <p
          ref={descriptionRef}
          className={C("body-md", S.clampedText)}
          data-pr-tooltip={isClamped ? description : undefined}
          data-pr-position="bottom"
          data-pr-classname={S.descriptionTooltip}
        >
          {description}
        </p>
        {isClamped && <Tooltip target={descriptionRef} style={{ maxWidth: "400px" }} />}
      </div>
      <div className={S.assessmentDetailsWrapper}>
        <div>
          <p className={C(S.mutedText, "body-sm")}>Finish Date</p>
          <p className="body-md">{formatDate(finishDate)}</p>
        </div>
        <div>
          <p className={C(S.mutedText, "body-sm")}>Progress</p>
          <p className="body-md">{progress}%</p>
        </div>
        <div>
          <p className={C(S.mutedText, "body-sm")}>Exercises</p>
          <p className="body-md">
            {completedExercises}/{exercisesCount}
          </p>
        </div>
      </div>
      <div className={S.assessmentCardFooter}>
        <Button
          label={enrolled ? "View Tournament" : "Enroll in Tournament"}
          loading={isEnrolling}
          onClick={e => {
            e.stopPropagation();
            if (enrolled) {
              navigateToTournament();
            } else {
              handleEnrollAndNavigate();
            }
          }}
        />
      </div>
    </div>
  );
};
